import { User } from "@/pages/Public/pages/Login/schema/login";

import { ListPersonSchedule } from "../../../../../schema/list";

export type OutboxSenderInfo = {
	userId: number;
	name: string;
	firstName: string;
	lastName: string;
	type: "email" | "linkedin";
	emailAddress?: string;
	emailId?: number;
	scheduleId: number;
	isEditable: boolean;
};

/** Sender identity without the schedule linkage — a read-only fallback for schedule-less rows. */
export type SenderIdentity = Pick<
	OutboxSenderInfo,
	"name" | "firstName" | "lastName" | "type" | "emailAddress"
>;

/** Who wrote the last outbound message on a lead, and when (PREC-4085). */
export type LastOutboundAuthor = SenderIdentity & { userId: number; sentAt: string | null };

/**
 * Finds the relevant schedule for the outbox sender display.
 * Priority:
 * 1. The current schedule that has a value in scheduledFor (pending/upcoming)
 * 2. The last schedule that has been processedAt or failedAt (already executed)
 */
export const getRelevantSchedule = (
	schedules?: ListPersonSchedule[]
): ListPersonSchedule | undefined => {
	if (!schedules || schedules.length === 0) {
		return undefined;
	}

	// First: find a scheduled record with scheduledFor set (not yet sent)
	const scheduledRecord = schedules
		.slice()
		.reverse()
		.find(
			(schedule) =>
				schedule.processingState === "scheduled" &&
				schedule.scheduledFor !== null &&
				schedule.publishedAt === null
		);

	if (scheduledRecord) {
		return scheduledRecord;
	}

	// Second: find the last record with processedAt or failedAt
	const processedOrFailedRecord = schedules
		.slice()
		.reverse()
		.find((schedule) => schedule.processedAt !== null || schedule.failedAt !== null);

	return processedOrFailedRecord;
};

/**
 * Resolves sender information from the relevant schedule record,
 * using the sending users to get name details.
 */
/**
 * Returns the IDs of all schedules that are editable (pending).
 * A schedule is editable when processingState === "scheduled" and scheduledFor is set.
 */
export const getEditableScheduleIds = (schedules?: ListPersonSchedule[]): number[] => {
	if (!schedules) {
		return [];
	}

	return schedules
		.filter(
			(schedule) => schedule.processingState === "scheduled" && schedule.scheduledFor !== null
		)
		.map((schedule) => schedule.id);
};

export const getOutboxSender = (
	schedules?: ListPersonSchedule[],
	sendingUsers?: User[]
): OutboxSenderInfo | undefined => {
	const relevantSchedule = getRelevantSchedule(schedules);

	if (!relevantSchedule) {
		return undefined;
	}

	const user = sendingUsers?.find((u) => u.id === relevantSchedule.userId);

	if (!user) {
		return undefined;
	}

	const isEmail = relevantSchedule.emailId !== null && relevantSchedule.emailId !== undefined;
	const isEditable =
		relevantSchedule.processingState === "scheduled" && relevantSchedule.scheduledFor !== null;

	return {
		userId: relevantSchedule.userId,
		name: `${user.firstName} ${user.lastName}`,
		firstName: user.firstName,
		lastName: user.lastName,
		type: isEmail ? "email" : "linkedin",
		emailAddress: relevantSchedule.email?.email,
		emailId: isEmail ? relevantSchedule.emailId : undefined,
		scheduleId: relevantSchedule.id,
		isEditable,
	};
};

const getScheduleTime = (schedule: ListPersonSchedule | undefined): number | null => {
	const stamp = schedule?.processedAt ?? schedule?.failedAt;

	if (!stamp) {
		return null;
	}

	const time = new Date(stamp).getTime();

	return Number.isNaN(time) ? null : time;
};

const isWrittenAfter = (
	author: LastOutboundAuthor,
	schedule: ListPersonSchedule | undefined
): boolean => {
	const scheduleTime = getScheduleTime(schedule);

	if (scheduleTime === null) {
		return true;
	}

	const authorTime = author.sentAt ? new Date(author.sentAt).getTime() : NaN;

	return !Number.isNaN(authorTime) && authorTime > scheduleTime;
};

/**
 * The sender the Outbox card shows next to its message preview (PREC-4085).
 *
 * While a step is still pending, the avatar is that step's sender — it is the switcher for who
 * will send next, so it stays editable. Once nothing is pending, the card previews the last
 * outbound message, and the avatar must be whoever wrote it: a prospect on two lists whose
 * campaigns share a sender pool gets every colleague's message recorded on both memberships,
 * and showing the list's campaign sender beside a colleague's text read as "KL wrote this".
 */
export const resolveOutboxCardSender = (
	schedules: ListPersonSchedule[] | undefined,
	sendingUsers: User[] | undefined,
	lastOutboundAuthor: LastOutboundAuthor | null | undefined
): OutboxSenderInfo | undefined => {
	const scheduled = getOutboxSender(schedules, sendingUsers);

	if (scheduled?.isEditable || !lastOutboundAuthor) {
		return scheduled;
	}

	if (!isWrittenAfter(lastOutboundAuthor, getRelevantSchedule(schedules))) {
		return scheduled;
	}

	const { sentAt: _sentAt, ...identity } = lastOutboundAuthor;

	return { ...identity, scheduleId: -1, isEditable: false };
};
